import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { lumi } from '../lib/lumi';

interface Artwork {
  _id: string;
  title: string;
  collection?: string;
  universe?: string;
  image: string;
  year?: number;
  technique?: string;
  dimensions?: string;
  price?: number;
}

interface Group {
  key: string;
  collection: string;
  artworks: Artwork[];
}

const PER_PAGE = 4;

const formatPrice = (price?: number) => {
  if (price === undefined || price === null) return null;
  return `${price.toFixed(0)} €`;
};

/** Une collection : 4 oeuvres affichees a la fois, avec navigation. */
function CollectionRow({ group }: {group: Group;}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(group.artworks.length / PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const slice = group.artworks.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);
  // On complete toujours a 4 emplacements pour garder l'alignement de la grille
  const slots: (Artwork | null)[] = [...slice];
  while (slots.length < PER_PAGE) slots.push(null);

  const hasMore = group.artworks.length > PER_PAGE;

  return (
    <div>
      <motion.header
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4 }}
        className="mb-5 sm:mb-8">

        <h3
          className="text-black/75"
          style={{
            fontFamily: 'inherit',
            fontSize: 'clamp(20px, 2.2vw, 26px)',
            fontWeight: 500,
            lineHeight: 1.25,
            color: 'rgba(0, 0, 0, 0.75)'
          }}>

          {group.collection}
        </h3>
        <p className="mt-1 text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-[#9C7A3C]">
          {group.artworks.length} œuvre{group.artworks.length > 1 ? 's' : ''}
        </p>
      </motion.header>

      {/* Toujours 4 oeuvres par ligne, y compris sur telephone et tablette */}
      <div className="grid grid-cols-4 gap-x-2 gap-y-8 sm:gap-x-4 sm:gap-y-10 lg:gap-x-6 lg:gap-y-12">
        {slots.map((artwork, index) =>
        artwork ?
        <motion.article
          key={artwork._id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.24) }}
          className="group">

              <Link to={`/artwork/${artwork._id}`} className="block overflow-hidden bg-[#F7F4EE]">
                <img
              src={artwork.image}
              alt={artwork.title}
              className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              loading={index < 4 ? 'eager' : 'lazy'}
              decoding="async" />

              </Link>
              <div className="pt-2 sm:pt-3">
                <Link to={`/artwork/${artwork._id}`}>
                  <h4
                className="text-[11px] font-light text-[#6E6659] transition-colors group-hover:text-[#9C7A3C] line-clamp-2"
                style={{
                  fontFamily: 'inherit',
                  fontSize: '11px',
                  fontWeight: 300,
                  lineHeight: 1.35,
                  color: '#6E6659'
                }}>

                    {artwork.title}
                  </h4>
                </Link>
                <p className="mt-1 hidden sm:block text-[12px] font-light text-[#6E6659] leading-snug">
                  {[artwork.year, artwork.technique, artwork.dimensions].filter(Boolean).join(' · ')}
                </p>
                {formatPrice(artwork.price) &&
            <p className="mt-1 hidden sm:block text-[11px] font-light text-[#6E6659]">
                    {formatPrice(artwork.price)}
                  </p>
            }
              </div>
            </motion.article> :

        // Emplacement reserve : la grille garde toujours 4 colonnes
        <div key={`slot-${index}`} aria-hidden="true" />

        )}
      </div>

      {/* Pagination sous les tableaux */}
      {hasMore &&
      <div className="mt-5 sm:mt-6 flex items-center justify-center gap-4">
          <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={safePage === 0}
          aria-label="Œuvres précédentes"
          className="inline-flex items-center gap-1 min-h-[40px] px-2 text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-[#1F1C19] disabled:opacity-30 hover:text-[#9C7A3C] transition-colors">

            <ChevronLeft size={14} />
            Précédent
          </button>
          <span className="text-[11px] tracking-[0.12em] text-[#6E6659] tabular-nums">
            {safePage + 1} / {totalPages}
          </span>
          <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={safePage >= totalPages - 1}
          aria-label="Œuvres suivantes"
          className="inline-flex items-center gap-1 min-h-[40px] px-2 text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-[#1F1C19] disabled:opacity-30 hover:text-[#9C7A3C] transition-colors">

            Suivant
            <ChevronRight size={14} />
          </button>
        </div>
      }
    </div>);

}

/**
 * Sous la section About de l'accueil : les oeuvres rangees dans une collection,
 * par 4, avec le nom de la collection en titre et une navigation par pages.
 */
export default function HomeCollectionGrid() {
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { list } = await lumi.entities.artwork.list({ sort: { createdAt: -1 }, limit: 300 });
        // Une oeuvre est visible sauf si elle a ete desactivee explicitement
        const filtered = (list || []).filter((artwork: any) => {
          if (artwork.isActive === false) return false;
          const name = artwork.collection || artwork.universe;
          return typeof name === 'string' && name.trim().length > 0;
        });
        setArtworks(filtered);
      } catch (error) {
        console.error('Erreur chargement des oeuvres de la page accueil:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const groups = useMemo(() => {
    const result: Group[] = [];
    artworks.forEach((artwork) => {
      const name = (artwork.collection || artwork.universe || '').trim();
      if (!name) return;
      let group = result.find((g) => g.key === name);
      if (!group) {
        group = { key: name, collection: name, artworks: [] };
        result.push(group);
      }
      group.artworks.push(artwork);
    });
    // Collections classees par ordre alphabetique
    result.sort((a, b) => a.collection.localeCompare(b.collection, 'fr', { sensitivity: 'base' }));
    return result;
  }, [artworks]);

  if (loading) return null;
  if (groups.length === 0) return null;

  return (
    <section className="py-8 sm:py-12 px-4 sm:px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        




















        <div className="space-y-10 sm:space-y-12">
          {groups.map((group) =>
          <CollectionRow key={group.key} group={group} />
          )}
        </div>
      </div>
    </section>);

}